/**
 * Generic Record Modal
 *
 * Dynamically creates/edits records for any object type based on field definitions.
 * Renders form fields based on the object's fields from Object Manager.
 */

import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { useFieldsByObject } from '../../services/queries/fieldQueries';
import { useCreateGenericRecord, useUpdateGenericRecord } from '../../services/queries/genericListViewQueries';
import { useToast } from '../../contexts/ToastContext';
import { getRecordById } from '../../services/recordStorageService';
import {
  StandardButton,
  StandardInput,
  StandardLookupField,
  StandardTextarea,
  StandardSelect,
  StandardCheckbox
} from '../standard';
import RichTextEditor from '../common/RichTextEditor';
import { getPicklistValuesAsStrings, type CustomField } from '../../services/queries/fieldQueries';

interface GenericRecordModalProps {
  objectApiName: string;
  objectId: string;
  objectLabel: string;
  isOpen: boolean;
  onClose: () => void;
  recordId?: string; // For editing existing records
  onRecordCreated?: () => void; // Callback when a record is successfully created
}

export const GenericRecordModal: React.FC<GenericRecordModalProps> = ({
  objectApiName,
  objectId,
  objectLabel,
  isOpen,
  onClose,
  recordId,
  onRecordCreated
}) => {
  const { success, error: showError } = useToast();
  const isEditMode = !!recordId;
  
  // Fetch fields for this object
  const { data: fields = [], isLoading: loadingFields } = useFieldsByObject(objectId, {
    includeInactive: false
  });
  
  useEffect(() => {}, [fields]);
  
  // Mutations
  const createMutation = useCreateGenericRecord(objectApiName);
  const updateMutation = useUpdateGenericRecord(objectApiName);
  
  // Form state - dynamically built from fields
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  
  // Load record data in edit mode
  useEffect(() => {
    // Don't run if modal is not open or fields are still loading
    if (!isOpen || loadingFields) {
      return;
    }

    if (isEditMode && recordId) {
      // Load record asynchronously - wait for fields to be ready
      getRecordById(objectApiName, recordId)
        .then((record) => {
          if (!record) {
            console.warn(`[GenericRecordModal] Record ${recordId} not found`);
            return;
          }

          // Populate form with record data
          const initialData: Record<string, any> = {};
          
          // First, populate all fields from the record
          // Match by exact field name (field names in records match field.fieldName exactly)
          fields.forEach((field: CustomField) => {
            // Try exact match first
            let value = record[field.fieldName];
            
            // If not found, try case-insensitive match
            if (value === undefined) {
              const recordKey = Object.keys(record).find(
                key => key.toLowerCase() === field.fieldName.toLowerCase()
              );
              if (recordKey) {
                value = record[recordKey];
              }
            }
            
            // Set value if found, otherwise use default
            if (value !== undefined && value !== null) {
              // Handle different data types appropriately
              if (field.fieldType === 'checkbox') {
                initialData[field.fieldName] = Boolean(value);
              } else if (field.fieldType === 'number' || field.fieldType === 'currency' || field.fieldType === 'percent') {
                initialData[field.fieldName] = value === '' ? 0 : Number(value);
              } else if (field.fieldType === 'date' && value) {
                // Ensure date is in YYYY-MM-DD format
                const dateValue = new Date(value);
                if (!isNaN(dateValue.getTime())) {
                  initialData[field.fieldName] = dateValue.toISOString().split('T')[0];
                } else {
                  initialData[field.fieldName] = value;
                }
              } else if (field.fieldType === 'datetime' && value) {
                // Ensure datetime is in format for datetime-local input
                const dateValue = new Date(value);
                if (!isNaN(dateValue.getTime())) {
                  initialData[field.fieldName] = dateValue.toISOString().slice(0, 16);
                } else {
                  initialData[field.fieldName] = value;
                }
              } else {
                initialData[field.fieldName] = value;
              }
              
            } else if (field.defaultValue !== undefined) {
              initialData[field.fieldName] = field.defaultValue;
            } else {
              // Set default based on field type
              switch (field.fieldType) {
                case 'checkbox':
                  initialData[field.fieldName] = false;
                  break;
                case 'number':
                case 'currency':
                case 'percent':
                  initialData[field.fieldName] = 0;
                  break;
                default:
                  initialData[field.fieldName] = '';
              }
            }
          });

          setFormData(initialData);
        })
        .catch((error) => {
          console.error('[GenericRecordModal] Failed to load record:', error);
          showError('Error', `Failed to load record: ${error.message || 'Unknown error'}`);
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    } else {
      // Reset form for create mode – apply field defaults so picklist default is selected
      const initialData: Record<string, any> = {};
      fields.forEach((field: CustomField) => {
        if (field.defaultValue !== undefined && field.defaultValue !== null && field.defaultValue !== '') {
          // Multipicklist expects an array; defaultValue from API is typically a string
          if (field.fieldType === 'multipicklist') {
            initialData[field.fieldName] = Array.isArray(field.defaultValue)
              ? field.defaultValue
              : [String(field.defaultValue)];
          } else {
            initialData[field.fieldName] = field.defaultValue;
          }
        } else {
          // Set default based on field type
          switch (field.fieldType) {
            case 'checkbox':
              initialData[field.fieldName] = false;
              break;
            case 'number':
            case 'currency':
            case 'percent':
              initialData[field.fieldName] = 0;
              break;
            case 'multipicklist':
              initialData[field.fieldName] = [];
              break;
            default:
              initialData[field.fieldName] = '';
          }
        }
      });
      setFormData(initialData);
    }
    setErrors({});
  }, [isEditMode, recordId, objectApiName, fields, isOpen, loadingFields, showError]);
  
  // Handle field change
  const handleFieldChange = (fieldName: string, value: any) => {
    setFormData(prev => ({ ...prev, [fieldName]: value }));
    // Clear error for this field
    if (errors[fieldName]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });
    }
  };
  
  // Validate form
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    fields.forEach((field: CustomField) => {
      if (field.required && field.isActive) {
        const value = formData[field.fieldName];
        if (value === undefined || value === null || value === '') {
          newErrors[field.fieldName] = `${field.fieldLabel} is required`;
        }
      }
      
      // Validate maxLength for text-based fields
      if (field.length && field.isActive) {
        const value = formData[field.fieldName];
        if (value !== undefined && value !== null && value !== '') {
          const stringValue = String(value);
          if (stringValue.length > field.length) {
            newErrors[field.fieldName] = `${field.fieldLabel} exceeds maximum length of ${field.length} characters`;
          }
        }
      }
    });
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  // Handle submit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      showError('Validation Error', 'Please fix the errors before submitting');
      return;
    }
    
    try {
      // Build payload with defaults for empty fields
      const payload: Record<string, any> = { ...formData };
      
      // Add default values for fields that are empty/undefined and have defaults
      fields.forEach((field: CustomField) => {
        const currentValue = payload[field.fieldName];
        // If field is empty/undefined and has a default value, add it to payload
        if ((currentValue === undefined || currentValue === null || currentValue === '') && 
            field.defaultValue !== undefined && field.defaultValue !== null && field.defaultValue !== '') {
          payload[field.fieldName] = field.defaultValue;
        }
      });
      
      if (isEditMode && recordId) {
        await updateMutation.mutateAsync({
          recordId,
          updates: payload
        });
        success('Record Updated', `${objectLabel} record has been updated`);
      } else {
        await createMutation.mutateAsync(payload);
        success('Record Created', `New ${objectLabel} record has been created`);
        // Call the onRecordCreated callback if provided (to refetch the list)
        if (onRecordCreated) {
          onRecordCreated();
        }
      }
      
      onClose();
    } catch (err: any) {
      console.error('Failed to save record:', err);
      showError('Error', err?.message || 'Failed to save record. Please try again.');
    }
  };
  
  // Render field input based on field type
  const renderFieldInput = (field: CustomField) => {
    const value = formData[field.fieldName] ?? '';
    const hasError = !!errors[field.fieldName];
    
    // Debug logging for rich text fields
    if (field.fieldType === 'richtextarea') { /* handled in switch */ }
    
    switch (field.fieldType) {
      case 'textarea':
      case 'longtextarea':
        // Get maxLength: use field.length if set, otherwise use defaults (1000 for textarea, 32000 for longtextarea)
        const maxLength = field.length || (field.fieldType === 'longtextarea' ? 32000 : 1000);
        return (
          <StandardTextarea
            label={field.fieldLabel}
            value={String(value)}
            onChange={(e) => handleFieldChange(field.fieldName, e.target.value)}
            required={field.required}
            error={hasError ? errors[field.fieldName] : undefined}
            placeholder={field.description || ''}
            rows={field.fieldType === 'longtextarea' ? 6 : 3}
            maxLength={maxLength}
            showCharCount={true}
          />
        );
        
      case 'richtextarea':
        return (
          <div key={`richtext-${field.fieldName}`} data-field-name={field.fieldName}>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {field.fieldLabel}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <RichTextEditor
              key={`richtext-editor-${field.fieldName}`}
              id={`richtext-toolbar-${field.fieldName}`}
              value={String(value || '')}
              onChange={(content) => handleFieldChange(field.fieldName, content)}
              placeholder={field.description || 'Start typing...'}
              height={300}
            />
            {hasError && errors[field.fieldName] && (
              <p className="text-red-500 text-sm mt-1">{errors[field.fieldName]}</p>
            )}
            {field.description && !hasError && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {field.description}
              </p>
            )}
          </div>
        );
        
      case 'picklist':
        return (
          <StandardSelect
            label={field.fieldLabel}
            value={String(value || '')}
            onChange={(e) => handleFieldChange(field.fieldName, e.target.value)}
            required={field.required}
            error={hasError ? errors[field.fieldName] : undefined}
            options={[
              { value: '', label: `Select ${field.fieldLabel}...` },
              ...getPicklistValuesAsStrings(field.picklistValues).map(val => ({ value: val, label: val }))
            ]}
          />
        );

      case 'multipicklist':
        return (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {field.fieldLabel}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <select
              multiple
              value={Array.isArray(value) ? value : (value ? [String(value)] : [])}
              onChange={(e) => {
                const selected = Array.from(e.target.selectedOptions, opt => opt.value);
                handleFieldChange(field.fieldName, selected);
              }}
              className={`
                w-full rounded-md border transition-colors appearance-none
                focus:outline-none focus:ring-2 focus:ring-offset-1
                ${hasError && errors[field.fieldName]
                  ? 'border-red-300 focus:border-red-500 focus:ring-red-500 dark:border-red-700'
                  : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500 dark:border-gray-600'
                }
                bg-white dark:bg-gray-800
                text-gray-900 dark:text-gray-100
                px-4 py-2 text-sm
                min-h-[100px]
              `}
              required={field.required}
            >
              {getPicklistValuesAsStrings(field.picklistValues).map(val => (
                <option key={val} value={val}>
                  {val}
                </option>
              ))}
            </select>
            {hasError && errors[field.fieldName] && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                {errors[field.fieldName]}
              </p>
            )}
            {field.description && !hasError && (
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                {field.description}
              </p>
            )}
          </div>
        );
        
      case 'checkbox':
        return (
          <StandardCheckbox
            label={field.fieldLabel + (field.required ? ' *' : '')}
            checked={Boolean(value)}
            onChange={(checked) => handleFieldChange(field.fieldName, checked)}
            error={hasError ? errors[field.fieldName] : undefined}
          />
        );
        
      case 'number':
      case 'currency':
      case 'percent':
        return (
          <StandardInput
            type="number"
            label={field.fieldLabel}
            value={String(value)}
            onChange={(e) => handleFieldChange(field.fieldName, e.target.value ? Number(e.target.value) : '')}
            required={field.required}
            error={hasError ? errors[field.fieldName] : undefined}
            placeholder={field.description || ''}
            step={field.fieldType === 'currency' ? '0.01' : '1'}
          />
        );
        
      case 'date':
        return (
          <StandardInput
            type="date"
            label={field.fieldLabel}
            value={value ? String(value).split('T')[0] : ''}
            onChange={(e) => handleFieldChange(field.fieldName, e.target.value)}
            required={field.required}
            error={hasError ? errors[field.fieldName] : undefined}
          />
        );
        
      case 'datetime':
        return (
          <StandardInput
            type="datetime-local"
            label={field.fieldLabel}
            value={value ? String(value).slice(0, 16) : ''}
            onChange={(e) => handleFieldChange(field.fieldName, e.target.value)}
            required={field.required}
            error={hasError ? errors[field.fieldName] : undefined}
          />
        );
        
      case 'email':
        return (
          <StandardInput
            type="email"
            label={field.fieldLabel}
            value={String(value)}
            onChange={(e) => handleFieldChange(field.fieldName, e.target.value)}
            required={field.required}
            error={hasError ? errors[field.fieldName] : undefined}
            placeholder={field.description || ''}
          />
        );
        
      case 'phone':
        return (
          <StandardInput
            type="tel"
            label={field.fieldLabel}
            value={String(value)}
            onChange={(e) => handleFieldChange(field.fieldName, e.target.value)}
            required={field.required}
            error={hasError ? errors[field.fieldName] : undefined}
            placeholder={field.description || ''}
          />
        );
        
      case 'lookup':
      case 'masterdetail':
        return (
          <StandardLookupField
            field={field}
            value={String(value || '')}
            onChange={(val) => handleFieldChange(field.fieldName, val)}
            error={hasError ? errors[field.fieldName] : undefined}
            required={field.required}
          />
        );
        
      default:
        return (
          <StandardInput
            label={field.fieldLabel}
            value={String(value)}
            onChange={(e) => handleFieldChange(field.fieldName, e.target.value)}
            required={field.required}
            error={hasError ? errors[field.fieldName] : undefined}
            placeholder={field.description || ''}
            maxLength={field.length}
          />
        );
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
              {isEditMode ? `Edit ${objectLabel}` : `New ${objectLabel}`}
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              {isEditMode ? 'Update record details' : 'Create a new record'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Content */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6">
          {loadingFields ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mb-4"></div>
              <p className="text-gray-600 dark:text-gray-400">Loading fields...</p>
            </div>
          ) : fields.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                No fields configured for this object.
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-500">
                Add fields to this object in Object Manager to create records.
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {fields.map((field: CustomField) => (
                <div key={field.id}>
                  {renderFieldInput(field)}
                  {/* Description is rendered inside renderFieldInput for richtextarea, so skip here */}
                  {field.fieldType !== 'richtextarea' && field.description && !errors[field.fieldName] && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {field.description}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </form>
        
        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700">
          <StandardButton
            variant="secondary"
            onClick={onClose}
            disabled={createMutation.isPending || updateMutation.isPending}
          >
            Cancel
          </StandardButton>
          <StandardButton
            variant="primary"
            onClick={handleSubmit}
            disabled={loadingFields || fields.length === 0 || createMutation.isPending || updateMutation.isPending}
          >
            {createMutation.isPending || updateMutation.isPending
              ? 'Saving...'
              : isEditMode
              ? 'Save Changes'
              : 'Create Record'}
          </StandardButton>
        </div>
      </div>
    </div>
  );
};
